// Constants for DOM elements
const SELECTORS = {
  CART: "#btn-open-cart",
  MOBILE: {
    MENU_BUTTON: "#mobile-menu-button",
    NAV_DRAWER: "#mobile-nav-drawer",
    CLOSE_BUTTON: "#close-mobile-nav",
    OVERLAY: "#mobile-nav-overlay",
    SUBMENU: ".mobile-submenu",
    MENU_LINK: ".mobile-menu-link.has-children",
    BACK_BUTTON: ".submenu-back",
    ACCOUNT_SECTION: ".account-section"
  },
  DESKTOP: {
    MENU_ITEM: ".header-menu-item",
    OVERLAY: "#overlay",
    DROPDOWN_MENU: ".dropdown-menu",
  },
};

// Utility functions
const utils = {
  showElement: (element, display = "block") => {
    if (!element) return;
    element.style.display = display;
    requestAnimationFrame(() => {
      element.style.opacity = "1";
    });
  },

  hideElement: (element) => {
    if (!element) return;
    element.style.opacity = "0";
    element.addEventListener("transitionend", function handler() {
      if (element.style.opacity === "0") {
        element.style.display = "none";
      }
      element.removeEventListener("transitionend", handler);
    });
  },

  resetSubmenus: () => {
    document.querySelectorAll(SELECTORS.MOBILE.SUBMENU).forEach((submenu) => {
      submenu.classList.remove("active");
    });
  },
};

// Mobile menu functionality
const initMobileMenu = () => {
  const {
    MENU_BUTTON,
    NAV_DRAWER,
    CLOSE_BUTTON,
    OVERLAY,
    MENU_LINK,
    BACK_BUTTON,
    ACCOUNT_SECTION
  } = SELECTORS.MOBILE;

  const elements = {
    menuButton: document.querySelector(MENU_BUTTON),
    navDrawer: document.querySelector(NAV_DRAWER),
    closeButton: document.querySelector(CLOSE_BUTTON),
    overlay: document.querySelector(OVERLAY),
    accountSection: document.querySelector(ACCOUNT_SECTION)
  };

  if (!Object.values(elements).every(Boolean)) return;

  const openMobileMenu = () => {
    utils.resetSubmenus();
    document.body.classList.add("modal-open");
    elements.overlay.classList.add("visible");
    utils.showElement(elements.navDrawer, "flex");
    // Show account section when opening main menu
    if (elements.accountSection) {
      elements.accountSection.style.display = "block";
    }
    requestAnimationFrame(() => {
      elements.navDrawer.classList.add("visible");
    });
  };

  const closeMobileMenu = () => {
    utils.resetSubmenus();
    elements.navDrawer.classList.remove("visible");
    elements.overlay.classList.remove("visible");
    setTimeout(() => {
      elements.navDrawer.style.display = "none";
      document.body.classList.remove("modal-open");
    }, 300);
  };

  // Event listeners for mobile menu
  elements.menuButton.addEventListener("click", openMobileMenu);
  elements.closeButton.addEventListener("click", closeMobileMenu);
  elements.overlay.addEventListener("click", closeMobileMenu);

  // Submenu functionality
  document.querySelectorAll(MENU_LINK).forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const idx = btn.getAttribute("data-submenu-index");
      document.getElementById(`submenu-${idx}`).classList.add("active");
      // Hide account section when opening submenu
      if (elements.accountSection) {
        elements.accountSection.style.display = "none";
      }
    });
  });

  document.querySelectorAll(BACK_BUTTON).forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const idx = btn.getAttribute("data-back-index");
      document.getElementById(`submenu-${idx}`).classList.remove("active");
      // Show account section when returning to main menu
      if (elements.accountSection) {
        elements.accountSection.style.display = "block";
      }
    });
  });
};

// Desktop menu functionality
const initDesktopMenu = () => {
  const { MENU_ITEM, OVERLAY, DROPDOWN_MENU } = SELECTORS.DESKTOP;
  const elements = {
    menuItems: document.querySelectorAll(MENU_ITEM),
    overlay: document.querySelector(OVERLAY),
    dropdownMenus: document.querySelectorAll(DROPDOWN_MENU),
    flyouts: document.querySelectorAll(".header-flyout"),
  };

  const handleMenuInteraction = (item, isEntering) => {
    const parentGroup = item.closest(".group");
    const dropdown = parentGroup?.querySelector(DROPDOWN_MENU);
    const flyout = parentGroup?.querySelector(".header-flyout");
    
    if (!dropdown && !flyout) return;

    if (isEntering) {
      utils.showElement(elements.overlay);
      item.classList.add("hover", "is--active");
      if (flyout) {
        flyout.classList.add("is--active");
      }
    } else {
      utils.hideElement(elements.overlay);
      item.classList.remove("hover", "is--active");
      if (flyout) {
        flyout.classList.remove("is--active");
      }
    }
  };

  // Menu items event listeners
  elements.menuItems.forEach((item) => {
    item.addEventListener("mouseenter", () =>
      handleMenuInteraction(item, true)
    );
    item.addEventListener("mouseleave", (e) => {
      const parentGroup = item.closest(".group");
      const dropdown = parentGroup?.querySelector(DROPDOWN_MENU);
      const flyout = parentGroup?.querySelector(".header-flyout");
      const targetElement = dropdown || flyout;
      
      if (!targetElement?.contains(e.relatedTarget)) {
        handleMenuInteraction(item, false);
      }
    });
  });

  // Dropdown menus event listeners
  elements.dropdownMenus.forEach((menu) => {
    menu.addEventListener("mouseenter", () => {
      utils.showElement(elements.overlay);
      const parentGroup = menu.closest(".group");
      const parentMenuItem = parentGroup?.querySelector(MENU_ITEM);
      parentMenuItem?.classList.add("hover");
    });

    menu.addEventListener("mouseleave", (e) => {
      const parentGroup = menu.closest(".group");
      if (!parentGroup?.contains(e.relatedTarget)) {
        utils.hideElement(elements.overlay);
        parentGroup?.querySelector(MENU_ITEM)?.classList.remove("hover");
      }
    });
  });

  // Header flyout event listeners (separate from dropdown-menu)
  elements.flyouts.forEach((flyout) => {
    flyout.addEventListener("mouseenter", () => {
      utils.showElement(elements.overlay);
      const parentGroup = flyout.closest(".group");
      const parentMenuItem = parentGroup?.querySelector(MENU_ITEM);
      parentMenuItem?.classList.add("hover", "is--active");
      flyout.classList.add("is--active");
    });

    flyout.addEventListener("mouseleave", (e) => {
      const parentGroup = flyout.closest(".group");
      if (!parentGroup?.contains(e.relatedTarget)) {
        utils.hideElement(elements.overlay);
        const parentMenuItem = parentGroup?.querySelector(MENU_ITEM);
        parentMenuItem?.classList.remove("hover", "is--active");
        flyout.classList.remove("is--active");
      }
    });
  });

  // Sync state for any menu item already under the cursor on page load
  elements.menuItems.forEach((item) => {
    if (item.matches(":hover")) {
      handleMenuInteraction(item, true);
    }
  });

  // Overlay event listeners
  if (elements.overlay) {
    elements.overlay.addEventListener("mouseenter", () =>
      utils.showElement(elements.overlay)
    );
    elements.overlay.addEventListener("mouseleave", (e) => {
      const menuItem = e.relatedTarget?.closest(MENU_ITEM);
      const hasDropdown = menuItem
        ?.closest(".group")
        ?.querySelector(DROPDOWN_MENU);
      if (!hasDropdown && !e.relatedTarget?.closest(DROPDOWN_MENU)) {
        utils.hideElement(elements.overlay);
      }
    });
  }
};

// Cart functionality
const initCart = () => {
  const cartButton = document.querySelector(SELECTORS.CART);
  if (cartButton) {
    cartButton.addEventListener("click", () => window.cartUi.openDrawer());
  }
};

// Initialize all functionality when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  initCart();
  initMobileMenu();
  initDesktopMenu();
});
